import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  Action,
  ActionCreator,
  Creator,
  DefaultProjectorFn,
  MemoizedSelector,
  Store,
} from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, of, Subject } from 'rxjs';
import { catchError, exhaustMap, finalize, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { UserNotificationAction } from '@arc/arc-shared-frontend';
import {
  LoadingSpinnerService,
  SelectedPaymentAccount,
  WalletContextService,
  WindowService,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectSessionId } from '../session-store';
import { selectTenantId } from '../external-selectors';
import {
  selectSessionPaymentAccount,
  selectUnenrolledAutopayAccounts,
} from '../btpay';

/**
 * Factory that creates a reusable NgRx functional effect for the BTPAY-7160 click-through
 * wallet handoff: POSTs the already-computed unenrolledAccounts (and optional payment-method
 * burn — BTPAY-7332) to session-service's wallet-context endpoint (BTPAY-7254 — session-service
 * builds and returns checkoutReturnUrl itself, from the session it already has, so Wallet's
 * "Back" button can return the buyer to a landing page that reloads the receipt from persisted
 * session data), then redirects (same tab, no token in the URL) to Wallet's checkout-origin
 * AutoPay enrollment page. On failure, shows a translated error toast and leaves the buyer on
 * the confirmation screen — no retry page, no navigation.
 *
 * Burn source (BTPAY-7332): prefers an optional flow-specific payment-account selector (Intl
 * passes selectPaymentMethod from btpayInternational) and falls back to PayStore
 * selectSessionPaymentAccount (domestic POI/POA). Session-service uses request burn first;
 * session PaymentAccount.Burn is only a fallback and can be null after fresh Twikey.
 *
 * Usage (POI, POA, international — each passes its own flow-specific "Enroll in Autopay"
 * click action as the trigger):
 *
 *   export const poiRedirectToWalletAutopayEffect = createRedirectToWalletAutopayEffect(
 *     PoiPaymentConfirmationComponentActions.enrollInAutopayClicked,
 *   );
 */
export function createRedirectToWalletAutopayEffect<
  A extends Action<string>,
>(
  triggerAction: ActionCreator<string, Creator<any[], A>>,
  paymentAccountSelector?: MemoizedSelector<
    object,
    SelectedPaymentAccount,
    DefaultProjectorFn<SelectedPaymentAccount>
  >,
) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      walletContextService = inject(WalletContextService),
      windowService = inject(WindowService),
      translateService = inject(TranslateService),
      loadingSpinnerService = inject(LoadingSpinnerService),
    ) =>
      actions$.pipe(
        ofType(triggerAction),
        concatLatestFrom(() => [
          store.select(selectTenantId),
          store.select(selectSessionId),
          store.select(selectUnenrolledAutopayAccounts),
          store.select(selectSessionPaymentAccount),
          ...(paymentAccountSelector
            ? [store.select(paymentAccountSelector)]
            : [of(null as SelectedPaymentAccount)]),
        ]),
        exhaustMap(
          ([
            ,
            tenantId,
            sessionId,
            unenrolledAccounts,
            sessionPaymentAccount,
            flowPaymentAccount,
          ]) => {
          // Fed to loadUntil below instead of the HTTP call itself, so the spinner is driven
          // by the single subscription this effect already makes rather than a second one —
          // loadUntil() subscribes independently, and re-subscribing a cold HTTP observable
          // would re-issue the POST.
          const settled$ = new Subject<void>();
          loadingSpinnerService.loadUntil(settled$);

          const paymentMethodBurn =
            flowPaymentAccount?.burn ?? sessionPaymentAccount?.burn;

          return walletContextService
            .createWalletContext(tenantId, sessionId, {
              unenrolledAccounts,
              ...(paymentMethodBurn ? { paymentMethodBurn } : {}),
            })
            .pipe(
              tap(() => {
                windowService.setLocation(
                  `/wallet/checkoutautopay/${sessionId}/${tenantId}`,
                );
              }),
              // No action to dispatch on success — the tap above already navigated away.
              switchMap(() => EMPTY),
              catchError(() =>
                of(
                  new UserNotificationAction({
                    type: 'error',
                    message: translateService.instant(
                      'ARC_PAYMENT_AUTOPAY_ENROLLMENT_REDIRECT_ERROR',
                    ) as string,
                  }),
                ),
              ),
              finalize(() => {
                settled$.next();
                settled$.complete();
              }),
            );
        },
        ),
      ),
    { functional: true },
  );
}
